/**
 * DailyMonitor.jsx
 * Real-time daily booking monitor.
 * Shows every apartment's status for TODAY at a glance.
 * Auto-refreshes every 60 seconds.
 */
import React, { useState, useEffect, useCallback } from "react";
import { bizFetch } from "../utils/bizApi";

const fmt = (n) => new Intl.NumberFormat("en-PK", {
  style: "currency", currency: "PKR", maximumFractionDigits: 0,
}).format(n || 0);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-PK", {
  day: "numeric", month: "short", year: "numeric",
}) : "—";

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString("en-PK", {
  hour: "2-digit", minute: "2-digit",
}) : "";

// ─── Determine a property's status for today ──────────────────────────────────
function getPropertyStatus(property, bookings) {
  const today     = new Date(); today.setHours(0,0,0,0);
  const tomorrow  = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);

  // Active booking right now
  const activeBooking = bookings.find(b => {
    if (b.property !== property) return false;
    if (b.status === "Cancelled") return false;
    const ci = new Date(b.checkIn);  ci.setHours(0,0,0,0);
    const co = new Date(b.checkOut); co.setHours(0,0,0,0);
    return ci <= today && co > today;
  });

  if (activeBooking) {
    const co       = new Date(activeBooking.checkOut); co.setHours(0,0,0,0);
    const daysLeft = Math.ceil((co - today) / (1000*60*60*24));
    return { status: "occupied", booking: activeBooking, daysLeft };
  }

  // Check-in today
  const checkInToday = bookings.find(b =>
    b.property === property && b.status !== "Cancelled" &&
    new Date(b.checkIn).toDateString() === today.toDateString()
  );
  if (checkInToday) return { status: "checking_in", booking: checkInToday };

  // Check-out today
  const checkOutToday = bookings.find(b =>
    b.property === property && b.status !== "Cancelled" &&
    new Date(b.checkOut).toDateString() === today.toDateString()
  );
  if (checkOutToday) return { status: "checking_out", booking: checkOutToday };

  // Next upcoming booking
  const nextBooking = bookings
    .filter(b => b.property === property && b.status !== "Cancelled" && new Date(b.checkIn) > today)
    .sort((a, b) => new Date(a.checkIn) - new Date(b.checkIn))[0];

  const daysUntil = nextBooking
    ? Math.ceil((new Date(nextBooking.checkIn).setHours(0,0,0,0) - today) / (1000*60*60*24))
    : null;

  return { status: "available", nextBooking, daysUntil };
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  occupied:     { label: "Occupied",     icon: "🔴", bg: "rgba(255,107,107,0.12)", border: "rgba(255,107,107,0.35)", color: "#ff6b6b",  dot: "#ff6b6b"  },
  checking_in:  { label: "Checking In",  icon: "🟡", bg: "rgba(212,168,83,0.12)",  border: "rgba(212,168,83,0.4)",  color: "#d4a853",  dot: "#d4a853"  },
  checking_out: { label: "Checking Out", icon: "🟠", bg: "rgba(249,115,22,0.12)",  border: "rgba(249,115,22,0.35)", color: "#f97316",  dot: "#f97316"  },
  available:    { label: "Available",    icon: "🟢", bg: "rgba(81,207,102,0.08)",  border: "rgba(81,207,102,0.25)", color: "#51cf66",  dot: "#51cf66"  },
};

// ─── Single property card ─────────────────────────────────────────────────────
function PropertyCard({ property, propData, info, onClick }) {
  const cfg = STATUS_CONFIG[info.status];

  return (
    <div
      onClick={() => onClick(property, info)}
      style={{
        background: cfg.bg,
        border: `1.5px solid ${cfg.border}`,
        borderRadius: "var(--radius-lg)",
        padding: "20px",
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.3)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Pulsing dot for occupied */}
      {info.status === "occupied" && (
        <div style={{ position: "absolute", top: 14, right: 14 }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: cfg.dot,
            boxShadow: `0 0 0 0 ${cfg.dot}`,
            animation: "pulse-ring 1.5s infinite",
          }} />
        </div>
      )}

      {/* Property photo or icon */}
      <div style={{
        width: "100%", height: 100,
        borderRadius: 10, marginBottom: 14,
        background: propData?.photoUrl
          ? `url(${propData.photoUrl}) center/cover`
          : "var(--bg-secondary)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: propData?.photoUrl ? 0 : 40,
        position: "relative",
      }}>
        {!propData?.photoUrl && "🏠"}
        {/* Status badge overlay */}
        <div style={{
          position: "absolute", bottom: 8, left: 8,
          background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
          borderRadius: 99, padding: "3px 10px",
          fontSize: 11, fontWeight: 700, color: cfg.color,
          display: "flex", alignItems: "center", gap: 5,
          border: `1px solid ${cfg.border}`,
        }}>
          <span style={{ fontSize: 8 }}>●</span>
          {cfg.label}
        </div>
      </div>

      {/* Property name */}
      <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10,
        color: "var(--text-primary)", whiteSpace: "nowrap",
        overflow: "hidden", textOverflow: "ellipsis" }}>
        {property}
      </h3>

      {/* Status details */}
      {info.status === "occupied" && info.booking && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 13 }}>👤</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{info.booking.guestName}</span>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-secondary)", display: "flex", flexDirection: "column", gap: 3 }}>
            <span>📅 Until: {fmtDate(info.booking.checkOut)}</span>
            <span>🌙 {info.daysLeft} night{info.daysLeft !== 1 ? "s" : ""} remaining</span>
            {(info.booking.remaining || 0) > 0 && (
              <span style={{ color: "#ff6b6b", fontWeight: 600 }}>
                ⏳ Due: {fmt(info.booking.remaining)}
              </span>
            )}
          </div>
        </div>
      )}

      {info.status === "checking_in" && info.booking && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 13 }}>👤</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{info.booking.guestName}</span>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-secondary)", display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ color: "#d4a853", fontWeight: 700 }}>✈️ Checking in TODAY</span>
            <span>📱 {info.booking.phone || "No phone"}</span>
            <span>👥 {info.booking.guests} guest{info.booking.guests > 1 ? "s" : ""}</span>
          </div>
        </div>
      )}

      {info.status === "checking_out" && info.booking && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 13 }}>👤</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{info.booking.guestName}</span>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-secondary)", display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ color: "#f97316", fontWeight: 700 }}>🏁 Checking out TODAY</span>
            {(info.booking.remaining || 0) > 0 && (
              <span style={{ color: "#ff6b6b", fontWeight: 600 }}>
                ⏳ Collect: {fmt(info.booking.remaining)}
              </span>
            )}
          </div>
        </div>
      )}

      {info.status === "available" && (
        <div style={{ fontSize: 11, color: "var(--text-secondary)", display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ color: "#51cf66", fontWeight: 700 }}>✅ Free today</span>
          {info.nextBooking ? (
            <span>📅 Next: {fmtDate(info.nextBooking.checkIn)} ({info.daysUntil}d away)</span>
          ) : (
            <span>📅 No upcoming bookings</span>
          )}
          {propData?.pricePerNight > 0 && (
            <span style={{ color: "#51cf66" }}>💰 PKR {Number(propData.pricePerNight).toLocaleString()}/night</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Detail drawer ────────────────────────────────────────────────────────────
function DetailDrawer({ property, info, onClose }) {
  const cfg     = STATUS_CONFIG[info.status];
  const booking = info.booking || info.nextBooking;

  return (
    <>
      <div onClick={onClose} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:200,backdropFilter:"blur(4px)" }} />
      <div style={{
        position:"fixed",top:0,right:0,bottom:0,width:"min(380px,100vw)",
        background:"var(--bg-card)",borderLeft:"1px solid var(--border)",
        zIndex:201,overflowY:"auto",animation:"slideInRight 0.3s ease",
        boxShadow:"-20px 0 60px rgba(0,0,0,0.4)",
      }}>
        {/* Header */}
        <div style={{ padding:"20px 24px 16px",borderBottom:"1px solid var(--border)",
          background:cfg.bg,display:"flex",justifyContent:"space-between",alignItems:"flex-start" }}>
          <div>
            <div style={{ fontSize:11,fontWeight:700,color:cfg.color,textTransform:"uppercase",
              letterSpacing:"0.08em",marginBottom:4 }}>{cfg.icon} {cfg.label}</div>
            <h2 style={{ fontSize:20,fontFamily:"var(--font-display)" }}>{property}</h2>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",
            color:"var(--text-muted)",cursor:"pointer",fontSize:20,padding:0 }}>✕</button>
        </div>

        <div style={{ padding:"20px 24px" }}>
          {booking ? (
            <>
              <p style={{ fontSize:11,fontWeight:700,color:"var(--text-secondary)",
                textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14 }}>
                Booking Details
              </p>
              {[
                { label:"Guest",        value: booking.guestName, icon:"👤" },
                { label:"Phone",        value: booking.phone || "—", icon:"📱" },
                { label:"Check-in",     value: fmtDate(booking.checkIn), icon:"📅" },
                { label:"Check-out",    value: fmtDate(booking.checkOut), icon:"📅" },
                { label:"Nights",       value: `${booking.nights} nights`, icon:"🌙" },
                { label:"Guests",       value: `${booking.guests} guest${booking.guests>1?"s":""}`, icon:"👥" },
                { label:"Source",       value: booking.source, icon:"📲" },
                { label:"Total Price",  value: fmt(booking.totalPrice), icon:"💰", color:"var(--accent-gold)" },
                { label:"Advance Paid", value: fmt(booking.advancePaid), icon:"✅", color:"var(--accent-green)" },
                { label:"Remaining",    value: fmt(booking.remaining), icon:"⏳",
                  color: (booking.remaining||0)>0 ? "var(--accent-rose)" : "var(--text-muted)" },
              ].map(row => (
                <div key={row.label} style={{ display:"flex",justifyContent:"space-between",
                  alignItems:"center",padding:"10px 0",
                  borderBottom:"1px solid var(--border)" }}>
                  <span style={{ fontSize:13,color:"var(--text-secondary)",display:"flex",alignItems:"center",gap:6 }}>
                    {row.icon} {row.label}
                  </span>
                  <span style={{ fontSize:13,fontWeight:600,color:row.color||"var(--text-primary)" }}>
                    {row.value}
                  </span>
                </div>
              ))}
              {booking.notes && (
                <div style={{ marginTop:14,background:"var(--bg-secondary)",borderRadius:8,
                  padding:"12px 14px",fontSize:13,color:"var(--text-secondary)" }}>
                  📝 {booking.notes}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign:"center",padding:"40px 0",color:"var(--text-muted)" }}>
              <div style={{ fontSize:48,marginBottom:12 }}>✅</div>
              <p style={{ fontSize:15,fontWeight:600,color:"var(--accent-green)" }}>Available Today</p>
              <p style={{ fontSize:13,marginTop:6 }}>No booking for this property today</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Main DailyMonitor ────────────────────────────────────────────────────────
export default function DailyMonitor({ bookings, properties }) {
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selected,    setSelected]    = useState(null); // { property, info }
  const [ticker,      setTicker]      = useState(0);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(new Date());
      setTicker(t => t + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Build status for each property
  const propertyStatuses = properties.map(prop => ({
    property: prop.name,
    propData: prop,
    info: getPropertyStatus(prop.name, bookings),
  }));

  // Summary counts
  const counts = {
    occupied:     propertyStatuses.filter(p => p.info.status === "occupied").length,
    checking_in:  propertyStatuses.filter(p => p.info.status === "checking_in").length,
    checking_out: propertyStatuses.filter(p => p.info.status === "checking_out").length,
    available:    propertyStatuses.filter(p => p.info.status === "available").length,
  };
  const total     = propertyStatuses.length;
  const bookedPct = total > 0 ? Math.round(((counts.occupied + counts.checking_in) / total) * 100) : 0;

  const today = new Date().toLocaleDateString("en-PK", {
    weekday:"long", day:"numeric", month:"long", year:"numeric",
  });

  return (
    <div>
      {/* ── Header bar ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start",
        marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:10,height:10,borderRadius:"50%",background:"#51cf66",
              animation:"pulse-ring 1.5s infinite" }} />
            <span style={{ fontSize:12,fontWeight:700,color:"#51cf66",textTransform:"uppercase",
              letterSpacing:"0.08em" }}>Live Monitor</span>
          </div>
          <p style={{ fontSize:13,color:"var(--text-muted)",marginTop:4 }}>
            Last updated: {fmtTime(lastRefresh)} · Auto-refreshes every 60s
          </p>
        </div>
        <div style={{ textAlign:"right" }}>
          <p style={{ fontSize:22,fontFamily:"var(--font-display)",fontWeight:700 }}>
            {today}
          </p>
        </div>
      </div>

      {/* ── Summary tiles ── */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",
        gap:14,marginBottom:28 }}>
        {[
          { label:"Total Properties",  value:total,              icon:"🏠", color:"var(--text-primary)",  bg:"var(--bg-card)" },
          { label:"Occupied",          value:counts.occupied,    icon:"🔴", color:"#ff6b6b",              bg:"rgba(255,107,107,0.1)"  },
          { label:"Checking In Today", value:counts.checking_in, icon:"🟡", color:"#d4a853",              bg:"rgba(212,168,83,0.1)"   },
          { label:"Checking Out",      value:counts.checking_out,icon:"🟠", color:"#f97316",              bg:"rgba(249,115,22,0.1)"   },
          { label:"Available",         value:counts.available,   icon:"🟢", color:"#51cf66",              bg:"rgba(81,207,102,0.08)"  },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding:"16px 18px",
            background:s.bg,border:"1px solid var(--border)",
            display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center" }}>
            <span style={{ fontSize:24,marginBottom:6 }}>{s.icon}</span>
            <p style={{ fontSize:28,fontFamily:"var(--font-display)",fontWeight:700,
              color:s.color,lineHeight:1 }}>{s.value}</p>
            <p style={{ fontSize:10,color:"var(--text-muted)",textTransform:"uppercase",
              letterSpacing:"0.06em",marginTop:4 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Occupancy bar ── */}
      <div className="card" style={{ padding:"16px 20px",marginBottom:28 }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
          <span style={{ fontSize:13,fontWeight:600 }}>Today's Occupancy Rate</span>
          <span style={{ fontSize:18,fontFamily:"var(--font-display)",fontWeight:700,
            color: bookedPct > 75 ? "#ff6b6b" : bookedPct > 40 ? "#d4a853" : "#51cf66" }}>
            {bookedPct}%
          </span>
        </div>
        <div style={{ background:"var(--bg-input)",borderRadius:99,height:10,overflow:"hidden" }}>
          <div style={{
            height:"100%",borderRadius:99,transition:"width 0.8s ease",
            width:`${bookedPct}%`,
            background: bookedPct > 75
              ? "linear-gradient(90deg,#ff6b6b,#f97316)"
              : bookedPct > 40
              ? "linear-gradient(90deg,#d4a853,#f97316)"
              : "linear-gradient(90deg,#51cf66,#4ecdc4)",
          }} />
        </div>
        <div style={{ display:"flex",justifyContent:"space-between",marginTop:6,
          fontSize:11,color:"var(--text-muted)" }}>
          <span>{counts.occupied + counts.checking_in} booked</span>
          <span>{counts.available} available</span>
        </div>
      </div>

      {/* ── Property cards ── */}
      {propertyStatuses.length === 0 ? (
        <div className="card" style={{ padding:60,textAlign:"center",color:"var(--text-muted)" }}>
          <div style={{ fontSize:48,marginBottom:12 }}>🏠</div>
          <p style={{ fontSize:15 }}>No properties found</p>
          <p style={{ fontSize:13,marginTop:6 }}>Add properties in the Properties tab first</p>
        </div>
      ) : (
        <>
          {/* Group by status for easy reading */}
          {[
            { key:"checking_in",  title:"✈️ Checking In Today" },
            { key:"checking_out", title:"🏁 Checking Out Today" },
            { key:"occupied",     title:"🔴 Currently Occupied" },
            { key:"available",    title:"🟢 Available Today" },
          ].map(group => {
            const cards = propertyStatuses.filter(p => p.info.status === group.key);
            if (cards.length === 0) return null;
            return (
              <div key={group.key} style={{ marginBottom:24 }}>
                <h3 style={{ fontSize:14,fontWeight:700,color:"var(--text-secondary)",
                  textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12,
                  display:"flex",alignItems:"center",gap:8 }}>
                  {group.title}
                  <span style={{ fontSize:11,background:"var(--bg-secondary)",
                    borderRadius:99,padding:"2px 8px",fontWeight:600 }}>
                    {cards.length}
                  </span>
                </h3>
                <div style={{ display:"grid",
                  gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:16 }}>
                  {cards.map(({ property, propData, info }) => (
                    <PropertyCard key={property} property={property}
                      propData={propData} info={info}
                      onClick={(p, i) => setSelected({ property:p, info:i })} />
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer property={selected.property} info={selected.info}
          onClose={() => setSelected(null)} />
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse-ring {
          0%   { box-shadow: 0 0 0 0 rgba(81,207,102,0.5); }
          70%  { box-shadow: 0 0 0 8px rgba(81,207,102,0); }
          100% { box-shadow: 0 0 0 0 rgba(81,207,102,0); }
        }
      `}</style>
    </div>
  );
}
